import type { EndorsementData, EntityData, IncludeFieldSet } from '@domain/types/entity';
import type { DocumentMode } from '@helpers/document-mode';
import { getResourcesListToFetch } from '@src/utils/get-resources-list-to-fetch';
import { queryOptions } from '@tanstack/react-query';
import axios from 'axios';

import { getCurrentDocumentApiUrl } from '../helpers/getCurrentDocumentApiUrl';
import { getRequestAuthPayload } from '../helpers/getRequestAuth';

import type { QueryFactory } from './factory.types';

interface FetchEntityOptions {
  dataRange: IncludeFieldSet[];
  /** Manually overwrite document mode auto-generated from the current pathname **/
  documentMode?: DocumentMode;
  entityId?: string;
}

type FetchEntity = (options: FetchEntityOptions) => Promise<EntityData>;

export const fetchEntity: FetchEntity = async (options) => {
  const pathname = location.pathname;
  const { dataRange, documentMode, entityId } = options;

  const params = new URLSearchParams({
    include: dataRange.join(','),
  });

  const url = getCurrentDocumentApiUrl(pathname, documentMode, entityId);

  const networkPayload = await getRequestAuthPayload({
    method: 'get',
    url,
    params,
  });

  const response = await axios(networkPayload);
  return response?.data;
};

/** Prefix of every audit-log query for an entity — invalidate on this to refetch the Activity panel. */
export const AUDIT_LOG_KEY = (entityId: string) => ['entity', entityId, 'audit_log'] as const;

export const entityQueries = {
  detail: (entityId: string) => {
    return queryOptions({
      queryKey: ['entity', entityId] as const,
      queryFn: () => fetchEntity({ dataRange: getResourcesListToFetch() }),
    });
  },
  // `version` (the contract's updated_at) is in the key so a contract change becomes a new query —
  // React Query returns pending instead of the stale prior audit log, avoiding a frame where the
  // rendered log is misaligned with the freshly-updated store blocks.
  auditLog: (entityId: string, version?: string) => {
    return queryOptions({
      queryKey: [...AUDIT_LOG_KEY(entityId), version] as const,
      queryFn: () => fetchEntity({ dataRange: ['audit_log'] }),
    });
  },
} satisfies QueryFactory;

type FetchEndorsementSummary = (options: FetchEntityOptions) => Promise<EndorsementData['summary']>;

export const fetchEndorsementSummary: FetchEndorsementSummary = async (options) => {
  const { dataRange, documentMode, entityId } = options;

  const params = new URLSearchParams({
    include: dataRange.join(','),
  });

  const url = getCurrentDocumentApiUrl(location.pathname, documentMode, entityId);

  const networkPayload = await getRequestAuthPayload({
    method: 'get',
    url: `${url}/summary`,
    params,
  });

  const response = await axios(networkPayload);
  return response?.data;
};
